/**
 * Order Panel Component for Placing Orders
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { placeOrder } from '../redux/slices/orderSlice';

const OrderPanel = ({ symbol, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.auth);
  
  // Initialize order data with null check
  const [orderData, setOrderData] = useState(() => ({
    exchange: symbol?.exchange || '',
    instrumentId: symbol?.instrumentId || '',
    tradingSymbol: symbol?.tradingSymbol || '',
    transactionType: symbol?.action || 'BUY',
    quantity: 1,
    product: 'INTRADAY',
    orderComplexity: 'REGULAR',
    orderType: 'MARKET',
    validity: 'DAY',
    price: '',
    slTriggerPrice: '',
    disclosedQuantity: '',
    deviceId: '123',
  }));

  const [message, setMessage] = useState({ type: '', text: '' });

  // Update order data when symbol changes
  React.useEffect(() => {
    if (symbol) {
      setOrderData(prev => ({
        ...prev,
        exchange: symbol.exchange || '',
        instrumentId: symbol.instrumentId || '',
        tradingSymbol: symbol.tradingSymbol || '',
        transactionType: symbol.action || 'BUY'
      }));
    }
  }, [symbol]);

  // Don't render if not open or no symbol
  if (!isOpen || !symbol) {
    return null;
  }

  const handleChange = (field, value) => {
    setOrderData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const submitData = { ...orderData, userId };
    
    const result = await dispatch(placeOrder(submitData));

    if (result.type === 'orders/placeOrder/fulfilled') {
      const orderId = result.payload?.data?.brokerOrderId || 'Unknown';
      setMessage({ type: 'success', text: `Order placed successfully! Order ID: ${orderId}` });
      setTimeout(() => onClose(), 2000);
    } else {
      setMessage({ type: 'error', text: result.payload?.error || result.error?.message || 'Failed to place order' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Place Order - {symbol?.tradingSymbol || 'Unknown Symbol'}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
                <select
                  value={orderData.transactionType}
                  onChange={(e) => handleChange('transactionType', e.target.value)}
                  className="input w-full"
                >
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  value={orderData.quantity}
                  onChange={(e) => handleChange('quantity', parseInt(e.target.value))}
                  className="input w-full"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                <select
                  value={orderData.product}
                  onChange={(e) => handleChange('product', e.target.value)}
                  className="input w-full"
                >
                  <option value="INTRADAY">Intraday</option>
                  <option value="LONGTERM">Delivery</option>
                  <option value="MTF">MTF</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order Type</label>
                <select
                  value={orderData.orderType}
                  onChange={(e) => handleChange('orderType', e.target.value)}
                  className="input w-full"
                >
                  <option value="MARKET">Market</option>
                  <option value="LIMIT">Limit</option>
                  <option value="SL">Stop Loss</option>
                  <option value="SLM">SL Market</option>
                </select>
              </div>

              {(orderData.orderType === 'LIMIT' || orderData.orderType === 'SL') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                  <input
                    type="number"
                    step="0.05"
                    value={orderData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    className="input w-full"
                    required
                  />
                </div>
              )}

              {(orderData.orderType === 'SL' || orderData.orderType === 'SLM') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Price</label>
                  <input
                    type="number"
                    step="0.05"
                    value={orderData.slTriggerPrice}
                    onChange={(e) => handleChange('slTriggerPrice', e.target.value)}
                    className="input w-full"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Validity</label>
                <select
                  value={orderData.validity}
                  onChange={(e) => handleChange('validity', e.target.value)}
                  className="input w-full"
                >
                  <option value="DAY">Day</option>
                  <option value="IOC">IOC</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disclosed Qty (Optional)</label>
                <input
                  type="number"
                  value={orderData.disclosedQuantity}
                  onChange={(e) => handleChange('disclosedQuantity', e.target.value)}
                  className="input w-full"
                  placeholder="0"
                />
              </div>
            </div>

            {message.text && (
              <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                {message.text}
              </div>
            )}

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                className={`btn flex-1 ${orderData.transactionType === 'BUY' ? 'btn-success' : 'btn-danger'}`}
              >
                {orderData.transactionType === 'BUY' ? 'BUY' : 'SELL'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderPanel;
